import { useLocation } from "react-router-dom";
import moment from "moment";
import { forEach } from "lodash";
import * as XLSX from 'xlsx';
import {
  DATE_FORMAT,
  MONTH_FORMAT,
  FULL_DATE_FORMAT,
  TIME_FORMAT,
  VACATIONS,
  TIME_24_HOUR_FORMAT,
  DAY_MONTH_FORMAT,
  FULL_DATE_WEEKDAY_FORMAT
} from "utility/helpers/metaData";

export const formatNumberWithCommas = (number) => {
  if (number) {
    number = number.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
  }
  return number;
};

export const extractUserCode = () => {
  const user = JSON.parse(localStorage.getItem("userDetails"));
  return user?.language?.code;
};

export const lazyLoadImage = (imageName) =>
  require(`assets/images/${imageName}`);

export const lazyLoadVideo = (videoName) =>
  require(`assets/videos/${videoName}`);

export const useQuery = (queryParam) => {
  const search = new URLSearchParams(useLocation().search);
  return search.get(queryParam);
};

// The selected entity is kept PER TAB in sessionStorage. localStorage is shared by every tab, so
// with two tabs on different entities the last one picked overwrote the other: a tab on
// Development showed Armenia's logo, and any page reached without ?tenantId= in its URL read -- and
// could save to -- the other tab's entity. localStorage still receives each selection, but only as
// the starting entity for a tab that has none yet (a new tab or window).
const readStored = (key) => window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
const storeForThisTab = (key, value) => {
  window.sessionStorage.setItem(key, value);
  window.localStorage.setItem(key, value);
};

/** The entity id this tab is working in, without looking at the URL. */
export const getTabTenantId = () => readStored("tenantId");

// tenantInfo is stored either as a tenant object or as a one-element array (AccountMenu stores a
// filtered list); every reader already handles both shapes.
const tenantIdOf = (tenantInfo) => (Array.isArray(tenantInfo) ? tenantInfo[0]?.id : tenantInfo?.id);

export const setCurrentTenant = (tenantInfo) => {
  storeForThisTab('tenantInfo', JSON.stringify(tenantInfo));
}

/**
 * The details (name, logo, theme colour) of the entity this tab is working in, as a JSON string.
 *
 * Always matches the tab's entity id: when the stored details belong to another entity -- the URL
 * switched this tab's entity, or the details came from the shared default -- they are looked up in
 * the tenant list instead. Falls back to the stored details when the entity is not in that list
 * (the super-admin Customers page selects entities that are not).
 */
export const getCurrentTenant = () => {
  const stored = readStored('tenantInfo');
  // Same precedence as getTenantId: the tab's URL, then the tab's own selection.
  const tabTenantId = new URLSearchParams(window.location.search).get('tenantId') || getTabTenantId();
  if (!tabTenantId) {
    return stored;
  }
  try {
    if (stored && tenantIdOf(JSON.parse(stored)) === tabTenantId) {
      return stored;
    }
    const fromList = (JSON.parse(getTenants()) || []).find((tenant) => tenant?.id === tabTenantId);
    return fromList ? JSON.stringify(fromList) : stored;
  } catch (e) {
    return stored;
  }
};

export const setTenants = (tenants) => {
  window.localStorage.setItem('tenants', JSON.stringify(tenants));
}

export const getTenants = () => window.localStorage.getItem('tenants');


export const setTenantId = (tenantId) => {
  storeForThisTab("tenantId", tenantId);
};

export const getTenantId = (location, queryParam) => {
  const search = new URLSearchParams(location.search);
  return search.get(queryParam) || getTabTenantId();
};

export const getTeamsName = (teams) => {
  let teamsName = "";
  if (teams && teams.length) {
    teamsName = teams.pop().team;
    if (teams.length) {
      teamsName += `( +${teams.length} Teams)`;
    }
  }
  return teamsName;
};

export const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = moment().add(days, "days");
    expires = moment.utc(date).format();
  }
  document.cookie = `${name}=${value || ""}; expires=${expires}; path=/`;
};

export const formatWorkingDays = (slotsPerDay, slots) => {
  const slotsLen = slots.length;
  let workingDays;
  if (slotsLen === 6 || slotsLen === 12) {
    workingDays = "Sat To Thu";
  } else if (slotsLen === 5 || slotsLen === 10) {
    if (isSunToThu(slotsPerDay)) {
      workingDays = "Sun To Thu";
    } else {
      workingDays = "Mon To Fri";
    }
  } else {
    workingDays = "Custom";
  }
  return workingDays;
};

export const formatSlots = (slots) => {
  let slotsText = "";

  slots.sort((a, b) => a.day - b.day);

  const formatTime = (hours, minutes) => {
    let amPm;
    let formattedHours = hours;
    let formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    if (hours < 12) {
      amPm = "AM";
    } else {
      amPm = "PM";
      if (hours > 12) formattedHours -= 12;
    }

    return `${formattedHours}:${formattedMinutes} ${amPm}`;
  };

  if (slots.length === 5 || slots.length === 6) {
    const firstSlot = slots[0];

    const startTime = formatTime(firstSlot.startSlotTimeHours, firstSlot.startSlotTimeMinutes);
    const endTime = formatTime(firstSlot.endSlotTimeHours, firstSlot.endSlotTimeMinutes);

    slotsText = `${startTime} - ${endTime}`;
  } else {
    const slot1 = slots[0];
    const slot2 = slots[1];

    const startTimeSlot1 = formatTime(slot1.startSlotTimeHours, slot1.startSlotTimeMinutes);
    const endTimeSlot1 = formatTime(slot1.endSlotTimeHours, slot1.endSlotTimeMinutes);

    const startTimeSlot2 = formatTime(slot2.startSlotTimeHours, slot2.startSlotTimeMinutes);
    const endTimeSlot2 = formatTime(slot2.endSlotTimeHours, slot2.endSlotTimeMinutes);

    slotsText = `${startTimeSlot1} - ${endTimeSlot1} / ${startTimeSlot2} - ${endTimeSlot2}`;
  }

  return slotsText;
};

const isSunToThu = (slotsPerDay) => {
  const sunToThuDays = [0, 1, 2, 3, 4];
  let sunToThu = true;
  forEach(slotsPerDay, ({ day, numberOfSlots }, index) => {
    if (numberOfSlots > 0) {
      if (sunToThuDays.indexOf(day) === -1) {
        sunToThu = false;
        return false;
      }
    }
  });
  return sunToThu;
};

export const formatTime = (date) => {
  return date ? moment.utc(date).format(TIME_FORMAT) : '--';
};

export const formatDate = (date) => {
  return date ? moment.utc(date).format(DATE_FORMAT) : '--';
};

export const formatMonth = (date) => {
  return moment.utc(date).format(MONTH_FORMAT);
};

export const formatFullDate = (date) => {
  return moment.utc(date).format(FULL_DATE_FORMAT);
};

export const formatFullDateWeekday= (date) => {
  return moment.utc(date).format(FULL_DATE_WEEKDAY_FORMAT);
};

export const formatFullDateLocal = (date) => {
  return moment(date).format(FULL_DATE_FORMAT);
};

export const formatFullDateWeekdayLocal = (date) => {
  return moment(date).format(FULL_DATE_WEEKDAY_FORMAT);
};

export const getDurationText = (time, showDetails) => {
  const duration = moment.duration(Math.abs(time));
  let durationText = "";
  if (duration.years()) {
    durationText += ` ${duration.years()} year${
      duration.years() > 1 ? "s" : ""
    }`;
  }
  if (duration.months()) {
    durationText += ` ${duration.months()} month${
      duration.months() > 1 ? "s" : ""
    }`;
  }
  if (duration.days()) {
    durationText += ` ${duration.days()} day${duration.days() > 1 ? "s" : ""}`;
  }
  if (showDetails && duration.hours()) {
    durationText += ` ${duration.hours()} hr${duration.hours() > 1 ? "s" : ""}`;
  }
  if (showDetails && duration.minutes()) {
    let minutes = duration.minutes();
    if (duration.seconds() >= 30) {
      minutes++;
    }
    durationText += ` ${minutes} min${
        minutes > 1 ? "s" : ""
    }`;
  }
  return durationText;
};

export const formatTimeOff = (days, hours) => {
  let durationText = "";

  if (days >= 1) {
    durationText = `${days.toFixed(2)} ${days === 1 ? "day" : "days"}`;
  } else if (hours >= 1) {
    let formattedHours = parseFloat(hours.toFixed(2));
    durationText = `${formattedHours} ${
      formattedHours === 1 ? "hour" : "hours"
    }`;
  } else {
    durationText = Math.ceil(hours * 60) + " minutes";
  }
  return durationText;
};

export const getDateOptions = () => {
  let years;
  years = [
    moment().subtract(2, "y"),
    moment().subtract(1, "y"),
    moment(),
    moment().add(1, "y"),
  ].map((year) => {
    const isCurrentYear = year.isSame(moment(), "year");
    return {
      label: isCurrentYear ? `${year.format("YYYY")} (Current)` : year.format("YYYY"),
      isCurrent: isCurrentYear,
      day: year.format(DATE_FORMAT),
      fromDate: year.startOf("y").valueOf(),
      toDate: year.endOf("y").valueOf(),
      value: year.format("YYYY")
    };
  });
  years.push({label: "Custom Date", value: "customDate"});
  return years;
};

export const isObjectEmpty = (objectName) => {
  // Check if objectName is undefined or null
  if (objectName === undefined || objectName === null) {
    return true; // Return true if object is empty
  }

  // Check if objectName is an empty object
  return Object.keys(objectName).length === 0;
};

//TODO: Move these funcs to utile
export const getStartEndOfYear = (year = moment().year()) => {
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  return { from, to };
};
export const getYears = (startYear, endYear = moment().year()) => {
  const years = Array.from({ length: endYear - startYear + 1 }, (_, index) => {
    const year = startYear + index;
    return { year: year, isCurrent: moment().year() === year }; // Modify this line to include additional properties
  });

  return years;
};

export const getHolidayDays = (fromDate, toDate) => {
  const startDate = moment(fromDate);
  const endDate = moment(toDate);

  const diffMonths = Math.abs(endDate.diff(startDate, "months"));
  const diffDays = Math.abs(endDate.diff(startDate, "days"));

  let endDateFormat = "";

  if (diffMonths > 0) {
    endDateFormat += "MMM,";
  }
  if (diffDays > 0) {
    endDateFormat += "DD";
  }

  const endDateDisplay = endDateFormat
    ? `to ${endDate.format(endDateFormat)}`
    : "";

  // Format the date range
  const formattedDateRange = endDateDisplay? `${startDate.format("MMM,DD")} ${endDateDisplay}` : `${startDate.format(DAY_MONTH_FORMAT)}`;

  return formattedDateRange;
};


export const timeSlots = (numSlots, hrFrom1, hrTo1, minFrom1, minTo1, hrFrom2, hrTo2, minFrom2, minTo2) => {
  let slots= [];
  
  // we have only two cases
  // 1. the working days with only one slot
  // 2. the working days with two slots
  let slot1 = {};
  slot1.startSlotTimeHours = hrFrom1;
  slot1.endSlotTimeHours = hrTo1;
  slot1.startSlotTimeMinutes = minFrom1;
  slot1.endSlotTimeMinutes = minTo1;
  slots.push(slot1);
  if (numSlots === 2) {
    let slot2 = {};
    slot2.startSlotTimeHours = hrFrom2;
    slot2.endSlotTimeHours = hrTo2;
    slot2.startSlotTimeMinutes = minFrom2;
    slot2.endSlotTimeMinutes = minTo2;
    slots.push(slot2);
  }
  

  return slots;
};

export const mapVacationType = (type) => {
  return VACATIONS[type] || 'Other';
}

export const downloadExcelFile = (data, fileName, sheetName) => {
  const myWorkBook = XLSX.utils.book_new();
  const myWorkSheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(myWorkBook, myWorkSheet, sheetName);
  XLSX.writeFile(myWorkBook, fileName);
}

export const getUnitOption = (t) =>  [
  { label:t('weeks'), value:'Weeks' },
  { label:t('days'), value:'Days' },
  { label:t('hours'), value:'Hours' }
];

export const convertTimeToDecimal = (timeString)=> {
  const [hours, minutes] = moment(timeString, TIME_FORMAT).format(TIME_24_HOUR_FORMAT).split(':');
  return parseInt(hours, 10) + parseInt(minutes, 10) / 60;
}

export const convertDecimalToTime = (decimalTime) => {
  const hours = Math.floor(decimalTime);
  const minutes = (decimalTime - hours) * 60;
  return moment({ hours, minutes });
}

export const getDayOrder = (date) => {
  const jsDate = moment(date)._d;
  const yearStart = new Date(jsDate.getFullYear(), 0, 0);
  const diff = jsDate - yearStart;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

export const getDateFromDayOrder = (dayOrder) => {
  const year = new Date().getFullYear(); // Get the current year
  const date = new Date(year, 0); // Initialize with the first day of the year
  date.setDate(dayOrder); // Set the day of the year
  return date;
};
export const getErrorMessage = (error) => {
  return error?.response?.data?.message || error?.response?.data || error?.message || 'Something went wrong';
};
export const durationSortComparator = (valueA, valueB) => {
  const parseDuration = (str) => {
    if (typeof str !== 'string') return 0;
    const text = str.toLowerCase();

    const days = parseFloat(text.match(/([\d.]+)\s*days?/)?.[1] || 0);
    const hours = parseFloat(text.match(/([\d.]+)\s*hours?/)?.[1] || 0);
    const minutes = parseFloat(text.match(/([\d.]+)\s*minutes?/)?.[1] || 0);

    return days * 1440 + hours * 60 + minutes;
  };

  return parseDuration(valueA) - parseDuration(valueB);
};

/* ------------------------------------------------------------------------------------------
 * Contract period helpers.
 *
 * The selected contract is the single source of the date range used to filter what the
 * Employee page shows. These three helpers define that rule once so every tab applies it
 * identically. All of them are no-ops when `contract` is falsy, which is what keeps employees
 * with one contract (or none) behaving exactly as they do today.
 * ---------------------------------------------------------------------------------------- */

/** Contract bounds as epoch millis, inclusive of both end days. */
export const getContractBounds = (contract) => {
  if (!contract) return { start: null, end: null };
  return {
    start: contract.startDate ? moment.utc(contract.startDate).startOf("day").valueOf() : null,
    end: contract.endDate ? moment.utc(contract.endDate).endOf("day").valueOf() : null,
  };
};

/**
 * Whether the Time Off date selector is on the current year -- Cancel and Delete are only enabled
 * then. The selector holds one of several shapes: an option with a `label` ("2026", or a custom
 * date such as "2026-05-15"), `{value: moment}`, or -- after a date is picked in the "to date"
 * picker -- a bare moment with no `label` at all. Calling `date?.label.includes(...)` on that last
 * shape threw "Cannot read properties of undefined (reading 'includes')".
 */
export const isCurrentYearSelected = (date) => {
  const currentYear = moment().year().toString();
  if (moment.isMoment(date)) return date.year().toString() === currentYear;
  if (moment.isMoment(date?.value)) return moment(date.value).year().toString() === currentYear;
  return String(date?.label ?? date?.value ?? '').includes(currentYear);
};

/**
 * What a "... until <when>" label should say: "Year End", or the selected contract's end date when
 * the contract stops first.
 *
 * The backend bounds every period figure at whichever comes first, the contract end or
 * 31 December (EmployeeServiceImp.periodEnd), so a contract ending mid-year earns nothing after
 * its last day. Saying "Year End" there promises accrual the number does not contain.
 *
 * `date` is the page's date selector in any of its shapes: a moment, `{value: moment}`, or
 * `{value: year}`. With no contract selected, or one running past 31 December, this returns
 * "Year End" and the wording is exactly what it always was.
 */
export const getPeriodEndLabel = (date, contract) => {
  const year = moment.isMoment(date)
    ? date.year()
    : moment.isMoment(date?.value)
      ? date.value.year()
      : Number(date?.value);
  if (!Number.isFinite(year)) return "Year End";

  const { end } = getContractBounds(contract);
  // Guard against the "no end date" sentinels that reach us as epoch 0 / year 1899.
  if (end === null || end <= 0 || moment.utc(end).year() <= 1900) return "Year End";

  // getContractBounds builds the bound in UTC, so compare and format in UTC: doing either in
  // local time renders the previous day anywhere east of GMT.
  return moment.utc(end).isBefore(moment.utc(`${year}-12-31`).endOf("day"))
    ? moment.utc(end).format(DATE_FORMAT)
    : "Year End";
};

/**
 * Narrows a requested [fromDate, toDate] range (epoch millis) to the contract period.
 * `empty` is true when the contract does not overlap the requested range at all, so callers
 * can show nothing instead of querying an inverted range.
 */
export const clampRangeToContract = (fromDate, toDate, contract) => {
  if (!contract) return { fromDate, toDate, empty: false };
  const { start, end } = getContractBounds(contract);
  let from = fromDate;
  let to = toDate;
  if (start !== null && (from === undefined || from === null || start > from)) from = start;
  if (end !== null && (to === undefined || to === null || end < to)) to = end;
  const empty = from !== null && to !== null && from > to;
  return { fromDate: from, toDate: to, empty };
};

/**
 * Normalises a record end date. Returns null for "still active", which includes a missing date
 * and the sentinel values this codebase uses for it (non-positive epochs and the legacy
 * 1899-12-31 placeholder seen in team history).
 */
const normalizeRecordEnd = (to) => {
  if (to === null || to === undefined || to === "") return null;
  const parsed = moment.utc(to);
  if (!parsed.isValid()) return null;
  if (parsed.valueOf() <= 0) return null;
  if (parsed.year() <= 1900) return null;
  return parsed.endOf("day").valueOf();
};

/**
 * True when a record's active period overlaps the contract period.
 *
 * Both the record and the contract are treated as possibly open-ended intervals:
 *   - a record with no end date is still active, so it extends indefinitely forward;
 *   - a record with no start date is treated as having always applied.
 *
 * A record therefore does NOT need to start inside the contract. One that began before the
 * contract and is still running (end date null) applies to it — for example a work-schedule
 * template assigned years ago and never closed.
 *
 * Records with no dates at all are kept, matching how the page treats undated rows today.
 */
export const overlapsContract = (from, to, contract) => {
  if (!contract) return true;
  const { start, end } = getContractBounds(contract);
  if (start === null && end === null) return true;

  const recordFrom = from ? moment.utc(from).startOf("day").valueOf() : null;
  const recordTo = normalizeRecordEnd(to);

  if (recordFrom === null && recordTo === null) return true;
  // Starts after the contract ended.
  if (end !== null && recordFrom !== null && recordFrom > end) return false;
  // Ended before the contract began. Skipped when the record is still active.
  if (start !== null && recordTo !== null && recordTo < start) return false;
  return true;
};

/** True when a month (e.g. "September") of `year` overlaps the contract period. */
export const isMonthWithinContract = (monthLabel, year, contract) => {
  if (!contract || !monthLabel) return true;
  const parsedYear = year ? moment.utc(String(year), "YYYY") : moment.utc();
  const month = moment.utc(`${monthLabel} ${parsedYear.format("YYYY")}`, "MMMM YYYY");
  if (!month.isValid()) return true;
  return overlapsContract(
    month.clone().startOf("month").valueOf(),
    month.clone().endOf("month").valueOf(),
    contract
  );
};
